import type { Trophic } from "./types";

/**
 * Carlson Trophic State Index classes.
 *
 * Status is driven by TSI(Secchi), not TSI(chlorophyll-a). Both are in the
 * base, but they disagree by a median of about 25 points here: the recorded
 * chlorophyll-a values run 0.01–1 µg/L, below what a lake plausibly holds and
 * below most analytical detection limits, which collapses 864 of 870
 * lake-years into "oligotrophic" and says nothing useful. Secchi is recorded
 * for every station-year, is the measurement the volunteers actually make,
 * and produces a distribution that separates these lakes. Chlorophyll-a is
 * still charted — it is just not allowed to set the headline.
 */
export const TROPHIC: Record<Trophic, {
  label: string; short: string; color: string; soft: string; blurb: string;
}> = {
  oligo: {
    label: "Oligotrophic",
    short: "Oligotrophic",
    color: "#0E6CA8",
    soft: "#E7F1F8",
    blurb: "Very clear water and low nutrient enrichment — TSI below 40.",
  },
  meso: {
    label: "Mesotrophic",
    short: "Mesotrophic",
    color: "#0B7A4B",
    soft: "#E6F2EC",
    blurb: "Moderately clear with some enrichment — TSI between 40 and 50.",
  },
  eutro: {
    label: "Eutrophic",
    short: "Eutrophic",
    color: "#C87E12",
    soft: "#FBF1E0",
    blurb: "Reduced clarity and higher productivity — TSI between 50 and 70.",
  },
  hyper: {
    label: "Hypereutrophic",
    short: "Hypereutrophic",
    color: "#A4161A",
    soft: "#FAE9EA",
    blurb: "Very low clarity and heavy algal productivity — TSI above 70.",
  },
  unknown: {
    label: "Not classified",
    short: "No index",
    color: "#8A93A6",
    soft: "#F0F2F6",
    blurb: "No usable Secchi reading, so no trophic index can be calculated.",
  },
};

export const TROPHIC_ORDER: Trophic[] = ["oligo", "meso", "eutro", "hyper", "unknown"];

/** Background bands for the TSI charts, matching the classes above. */
export const TSI_BANDS = [
  { from: 0,  to: 40,  color: "#E7F1F8", label: "Oligotrophic" },
  { from: 40, to: 50,  color: "#E6F2EC", label: "Mesotrophic" },
  { from: 50, to: 70,  color: "#FBF1E0", label: "Eutrophic" },
  { from: 70, to: 100, color: "#FAE9EA", label: "Hypereutrophic" },
];
